import React, { useContext } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import firebase from "../../firebase";
import { SnackbarAlert } from "../SnackbarAlert";
import { AuthContext } from "../../auth";

import { makeStyles, withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import InputAdornment from "@material-ui/core/InputAdornment";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import CancelRoundedIcon from "@material-ui/icons/CancelRounded";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";

const useStyles = makeStyles((theme) => ({
  input: {
    margin: 8,
  },
  button: {
    textTransform: "none",
    padding: theme.spacing(1),
    borderRadius: 8,
    fontSize: "1.3em",
    fontWeight: 600,
    letterSpacing: 1,
  },
  buttonComment: {
    color: theme.palette.primary.main,
  },
  buttonReply: {
    color: theme.palette.secondary.main,
  },
  dialog: {
    padding: theme.spacing(1),
    width: "auto",
    color: theme.palette.common.white,
  },
  dialogActionsComment: {
    background: "hsl(0, 0%, 97%)",
  },
  dialogActionsReply: {
    background: "hsl(0, 0%, 97%)",
  },
  dialogTitleComment: {
    background: "linear-gradient(to right bottom, #03ceac, #03bc9b)",
  },
  dialogTitleReply: {
    background: "linear-gradient(to right bottom, #3b5998, #35508e)",
  },
}));

const styles = (theme) => ({
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[50],
    fontSize: 40,
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, action, className, classes, ...other } = props;
  return (
    <MuiDialogTitle disableTypography {...other} className={className}>
      <Typography component="div">
        <Box
          fontSize={24}
          fontWeight="fontWeightBold"
          letterSpacing={0.7}
          p={1}
        >
          {children}
        </Box>
      </Typography>
      {action ? (
        <span>
          <Tooltip
            title="Close"
            enterDelay={300}
            leaveDelay={200}
            enterNextDelay={300}
          >
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={action}
            >
              <CancelRoundedIcon />
            </IconButton>
          </Tooltip>
        </span>
      ) : null}
    </MuiDialogTitle>
  );
});

const EditCommentForm = (props) => {
  const classes = useStyles();
  const { currentUser } = useContext(AuthContext);
  const { setSnackbarState } = useContext(SnackbarAlert);

  return (
    <Formik
      initialValues={{
        inputComment: props.comment,
      }}
      validationSchema={Yup.object().shape({
        inputComment: Yup.string().required(),
      })}
      onSubmit={async (values, actions) => {
        try {
          let commentData = {};
          if (props.replyTo) {
            commentData = {
              comment: values.inputComment,
              commentDate: props.commentTimestamp,
              commentEdited: true,
              commentName: props.commentName,
              commentUserID: props.commentUserID,
              fromPost: props.fromPost,
              voters: props.voters,
              votes: props.votes,
              replyTo: props.replyTo,
            };
          } else {
            commentData = {
              comment: values.inputComment,
              commentDate: props.commentTimestamp,
              commentEdited: true,
              commentName: props.commentName,
              commentUserID: props.commentUserID,
              fromPost: props.fromPost,
              voters: props.voters,
              votes: props.votes,
            };
          }
          let updates = {};
          updates["/comments/" + props.fromPost + "/" + props.id] = commentData;
          await firebase.database().ref().update(updates);
          setTimeout(() => {
            actions.resetForm();
            props.close();
            setSnackbarState({
              state: true,
              message: "Successfully updated! 👍",
              status: "info",
            });
          }, 0);
        } catch (error) {
          //handle error
          actions.resetForm();
          props.close();
          //console.log(error);
          setSnackbarState({
            state: true,
            message: "Something went wrong. 😟",
            status: "error",
          });
        }
      }}
    >
      {(formik) => (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
          key={props.id}
          noValidate
        >
          <DialogTitle
            id="form-dialog-title"
            action={props.close}
            className={clsx(
              classes.dialog,
              props.replyTo
                ? classes.dialogTitleReply
                : classes.dialogTitleComment
            )}
          >
            {props.replyTo ? (
              <span>Edit Reply</span>
            ) : (
              <span>Edit Comment</span>
            )}
          </DialogTitle>
          <DialogContent>
            <OutlinedInput
              color={props.replyTo ? "secondary" : "primary"}
              id="inputComment"
              name="inputComment"
              type="text"
              placeholder={
                props.replyTo
                  ? `Reply on ${props.replyTo}'s comment.`
                  : "Comment on this post."
              }
              startAdornment={
                <InputAdornment position="start">
                  {props.replyTo ? props.replyTo : ""}
                </InputAdornment>
              }
              {...formik.getFieldProps("inputComment")}
              autoFocus
              multiline
              fullWidth
            />
          </DialogContent>

          <DialogActions
            className={clsx(
              classes.dialog,
              props.replyTo
                ? classes.dialogActionsReply
                : classes.dialogActionsComment
            )}
          >
            <Button
              variant="text"
              type="submit"
              className={clsx(
                classes.button,
                props.replyTo ? classes.buttonReply : classes.buttonComment
              )}
              disabled={formik.isSubmitting}
              fullWidth
            >
              Save Changes
            </Button>
          </DialogActions>
        </form>
      )}
    </Formik>
  );
};

export default EditCommentForm;
